<template>
    <div>
        <v-row>
            <v-col cols="12" class="d-flex align-center justify-space-between pt-0">
                <h1 class="text-h4 mb-2">Geração de boletos de cobrança</h1>
            </v-col>
        </v-row>

        <v-alert v-if="error" type="error" closable class="mb-4">
            {{ error }}
        </v-alert>

        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

        <v-card>
            <v-card-text>
                <v-form @submit.prevent="gerar">
                    <v-row>
                        <v-col cols="6">
                            <v-text-field :value="transformToDateBr(mesReferencia)" placeholder="Mês referência" dense
                                readonly hide-details aria-label="Mês referência" />
                        </v-col>

                        <v-col cols="6">
                            <v-text-field v-model="vencimento" label="Vencimento" type="date"
                                :disabled="saving || loading" dense></v-text-field>
                        </v-col>

                        <!-- Comissões: Água e Gás -->
                        <v-col cols="6">
                            <text-field-money :value="valorComissaoAguaStr" readonly disabled=""
                                label="Valor da Comissão (Água)" prefix="R$"></text-field-money>
                        </v-col>

                        <v-col cols="6">
                            <text-field-money :value="valorComissaoGasStr" disabled readonly
                                label="Valor da Comissão (Gás)" prefix="R$"></text-field-money>
                        </v-col>

                        <v-col cols="12" class="d-flex">
                            <v-btn color="grey-darken-1" variant="text" class="mr-2" @click="resetar"
                                :disabled="saving || loading">
                                <v-icon left>mdi-restart</v-icon>
                            </v-btn>

                            <v-spacer></v-spacer>

                            <v-btn color="primary" type="submit" :loading="saving" :disabled="saving || loading">
                                Gerar
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
        </v-card>

        <!-- info status do processo -->
        <v-card v-if="!!processId || !!processStatus" class="mt-4">
            <v-card-text>
                <h3>Status do processo de geração de boletos #{{ processId }}</h3>

                <p>
                    Status: {{ getLegendProcess(processStatus) }}
                </p>

                <p class="text-caption">
                    {{ processMessage }}
                </p>
            </v-card-text>

            <v-progress-linear v-if="processStatus === 'processing' || processStatus === 'pending'" indeterminate
                color="primary"></v-progress-linear>
        </v-card>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';

import { useApi } from '@/utils/axios'
import { FINANCEIRO_ROTAS } from '@/utils/apiRoutes';
import { useNotificationStore } from '@/stores/notification';
import TextFieldMoney from '@/components/inputs/TextFieldMoney.vue';

// Initialize stores
const notificationStore = useNotificationStore();

const mesReferencia = ref('');
const vencimento = ref('');

const processId = ref(null);
const processStatus = ref(null); // pending, completed, failed
const processMessage = ref('');

const getLegendProcess = (processStatusParam) => {
    if (processStatusParam === 'pending') {
        return 'Pendente';
    } else if (processStatusParam === 'processing') {
        return 'Em processamento';
    } else if (processStatusParam === 'completed') {
        return 'Concluído';
    } else if (processStatusParam === 'failed') {
        return 'Falhou';
    } else {
        return '';
    }
}

// money strings (display) and digits in cents if needed
// substitui o único valor de comissão por dois: água e gás
const valorComissaoAguaStr = ref('0,00'); // read-only
const valorComissaoGasStr = ref('0,00');  // read-only

// states
const loading = ref(false);
const saving = ref(false);
const error = ref('');

// armazenamento do id do processo
const STORAGE_KEY = 'geracao_boletos_process_id';
let pollingTimer = null;

// helper to format from cents -> "1.234,56"
const formatFromDigits = (d) => {
    if (!d || d.length === 0) return '0,00';
    const decPart = d.slice(-2).padStart(2, '0');
    const intPart = d.slice(0, -2) || '0';
    const intWithSep = String(intPart).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${intWithSep},${decPart}`;
};

const fetchInitial = () => {
    return useApi(FINANCEIRO_ROTAS.COMISSOES).fetch()
};

const generateBoletos = (payload) => {
    return useApi(FINANCEIRO_ROTAS.GERAR_BOLETO_PS).post(payload)
};

// Fetch status do backend
const fetchProcessStatus = async (id) => {
    try {
        return await useApi(FINANCEIRO_ROTAS.STATUS_PROCESSO(id)).fetch();
    } catch (err) {
        // Retorna objeto com error para o chamador decidir como proceder
        return { error: err };
    }
};

const carregar = async () => {
    try {
        loading.value = true;
        const res = await fetchInitial();
        if (res && res.success) {
            const comA = res.data.comissao_agua ?? 0;
            const comG = res.data.comissao ?? 0;

            // convert to cents strings e popula displays
            const comACents = String(Math.round(Number(comA) * 100));
            const comGCents = String(Math.round(Number(comG) * 100));

            valorComissaoAguaStr.value = formatFromDigits(comACents);
            valorComissaoGasStr.value = formatFromDigits(comGCents);
            error.value = '';
        } else {
            throw new Error('Erro no response');
        }
    } catch (err) {
        error.value = 'Erro ao carregar valores iniciais.';
        notificationStore.showNotification(err?.response?.data?.message || 'Erro ao carregar valores iniciais', 'error');
    } finally {
        loading.value = false;
    }
};

const getDateFirstDayOfPreviousMonth = () => {
    const now = new Date();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = now.getMonth() === 0 ? 12 : now.getMonth();
    return `${year}-${String(month).padStart(2, '0')}-01`;
};

const transformToDateBr = (dateStr /* 2025-12-01 */) => {
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const getFifthDayOfCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    return `${year}-${String(month).padStart(2, '0')}-05`;
};

const stopPollingProcess = () => {
    if (pollingTimer) {
        clearInterval(pollingTimer);
        pollingTimer = null;
    }
    // limpar variáveis e storage
    localStorage.removeItem(STORAGE_KEY);
};

const handleStatusResponse = (res) => {
    // resposta inesperada -> parar
    if (!res || res.error || !res.success) {
        // se houver erro de rede, mantemos o polling (opcional).
        // aqui escolhemos parar apenas se a resposta estiver estruturalmente inválida
        return;
    }

    const status = res.data?.status;
    processStatus.value = status;

    if (status === 'processing' || status === 'pending') {
        // nada a fazer, continua a contagem
        return;
    }

    if (status === 'completed') {
        processMessage.value = res.data?.message || '';
        notificationStore.showNotification(res.data?.message || 'Processo de geração de boletos concluído.', 'success');
        stopPollingProcess();
        return;
    }

    if (status === 'failed') {
        processMessage.value = res.data?.message || '';
        notificationStore.showNotification(res.data?.message || 'Falha no processo de geração de boletos.', 'error');
        stopPollingProcess();
        return;
    }

    // status diferente -> limpar e parar
    stopPollingProcess();
};

const startPollingProcess = (id) => {
    // garante que não haja timers duplicados
    if (!id) return;
    if (pollingTimer) clearInterval(pollingTimer);
    processId.value = String(id);
    processStatus.value = 'processing';
    localStorage.setItem(STORAGE_KEY, String(id));

    // chamada imediata
    (async () => {
        const res = await fetchProcessStatus(processId.value);
        if (!res?.error) handleStatusResponse(res);
    })();

    // polling a cada 20s
    pollingTimer = setInterval(async () => {
        const res = await fetchProcessStatus(processId.value);
        if (!res?.error) handleStatusResponse(res);
    }, 20_000);
};

onMounted(async () => {
    await carregar();

    const firstDayPrevMonth = getDateFirstDayOfPreviousMonth();
    mesReferencia.value = firstDayPrevMonth;

    const fifthDayCurrentMonth = getFifthDayOfCurrentMonth();
    vencimento.value = fifthDayCurrentMonth;

    // se existir id salvo, inicia polling
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
        startPollingProcess(saved);
    }
});

onUnmounted(() => {
    // limpa interval quando componente desmontar
    stopPollingProcess();
});

const resetar = () => {
    vencimento.value = '';
};

const gerar = async () => {
    try {
        if (!mesReferencia.value) {
            notificationStore.showNotification('Informe o mês referência', 'error');
            return;
        }
        if (!vencimento.value) {
            notificationStore.showNotification('Informe o vencimento', 'error');
            return;
        }

        const payload = {
            mes_referencia: mesReferencia.value,
            vencimento: vencimento.value,
        };

        saving.value = true;
        const res = await generateBoletos(payload);
        if (res && res.success) {
            notificationStore.showNotification(res?.data?.message || 'Boletos gerados com sucesso.', 'success');

            // inicia monitoramento do processo de geração de boletos
            const id = res.data.process_id;
            // guarda e inicia polling
            startPollingProcess(id);
        } else {
            notificationStore.showNotification('Erro ao gerar boletos', 'error');
        }
    } catch (err) {
        const existErrorSpecificVencimento = !!err?.response?.data?.errors?.vencimento?.length;
        notificationStore.showNotification(
            existErrorSpecificVencimento
                ? err.response.data.errors.vencimento[0]
                :
                err?.response?.data?.message || 'Erro ao gerar boletos', 'error');
    } finally {
        saving.value = false;
    }
};
</script>

<style scoped>
.v-expansion-panel-title {
    padding: 10px 15px;
}

.action-icon {
    margin-left: 6px;
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.2s, transform 0.2s;
}

.action-icon:hover {
    opacity: 1;
    transform: scale(1.2);
}
</style>